'use client';

import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useSession } from 'next-auth/react';
import { formatDistanceToNowStrict } from 'date-fns';
import { useCallback, useEffect, type JSX } from 'react';
import { useMutation } from '@tanstack/react-query';

import { cn, parseUtcDateString } from '~/utils';
import { useIsMobile } from '~/hooks/useResponsive';
import {
  Dot,
  Show,
  Text,
  Tabs,
  XIcon,
  HStack,
  Button,
  Avatar,
  Popover,
  TabsList,
  BellIcon,
  AvatarImage,
  TabsTrigger,
  TabsContent,
  PopoverTrigger,
  PopoverContent,
  AvatarFallback,
  CircleCheckIcon,
} from '~/components';
import { getQueryClient } from '~/utils/queryClient';
import {
  notiApi,
  QK_FETCH_NOTIFICATIONS,
  type IGetNotiResponse,
  type IMarkAsReadRequest,
  type IRegisterDeviceRequest,
} from '~/requests/notiApi';

interface INotificationCardProps {
  time: string;
  read: boolean;
  message: string;
  avatar?: string;
  onClick?: () => void;
}

export const NotificationCard = ({
  time,
  message,
  avatar = '',
  read = true,
  onClick = () => {},
}: INotificationCardProps): JSX.Element => {
  return (
    <HStack
      gap={3}
      onClick={onClick}
      className='px-4 py-1 items-center mb-4 last:mb-0 cursor-pointer hover:bg-[#f6f7f8]'
    >
      <Avatar className='size-10'>
        <AvatarImage className='object-cover' src={avatar} />
        <AvatarFallback>
          <CircleCheckIcon />
        </AvatarFallback>
      </Avatar>
      <Text
        size='sm'
        className={cn('max-w-[230px] flex-1', 'md:max-w-[270px]')}
      >
        {message}
      </Text>
      <Text
        size='xs'
        className={cn(
          'text-dimGray-500 hidden min-w-[70px] w-max ml-auto',
          'md:block',
        )}
      >
        {formatDistanceToNowStrict(parseUtcDateString(time), {
          addSuffix: true,
        })}
      </Text>
      <Show when={!read}>
        <div
          className={cn(
            'relative size-[10px] bg-blueBonnet-500 rounded-full bottom-0.5',
            'md:ml-2',
          )}
        />
      </Show>
    </HStack>
  );
};

interface INotificationDropdownProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  notifications: IGetNotiResponse[];
}

export function NotificationDropdown({
  open,
  setOpen,
  notifications,
}: INotificationDropdownProps): JSX.Element {
  const router = useRouter();
  const isMobile = useIsMobile();
  const queryClient = getQueryClient();
  const { data: session } = useSession();
  const t = useTranslations('Home');
  const unreadNotifications = notifications.filter((noti) => !noti.read);

  const { mutate: registerDevice } = useMutation({
    mutationFn: async (payload: IRegisterDeviceRequest) => {
      return notiApi.registerDevice({ token: payload.token });
    },
    onSuccess: () => {
      console.log('Device registered successfully');
    },
    onError: (error) => {
      console.error('Failed to register device', error);
    },
  });

  useEffect(() => {
    if (session?.accessToken) {
      registerDevice({ token: session.accessToken });
    }
  }, [session?.accessToken, registerDevice]);

  const { mutate: markNotiAsRead } = useMutation({
    mutationFn: async (payload: IMarkAsReadRequest) => {
      return notiApi.markAsRead({ id: payload.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QK_FETCH_NOTIFICATIONS],
      });
    },
  });

  const { mutate: markAllNotiAsRead } = useMutation({
    mutationFn: async () => {
      return notiApi.markAllAsRead();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QK_FETCH_NOTIFICATIONS],
      });
    },
  });

  const handleNotiClick = useCallback(
    async (id: string, read: boolean, url?: string) => {
      if (!read) await markNotiAsRead({ id });
      if (url) router.push(url);
    },
    [markNotiAsRead, router],
  );

  const tabs = [
    {
      value: 'noti-all',
      label: t('all'),
      content: notifications.map(({ id, message, createdAt, read, url }) => (
        <NotificationCard
          key={id}
          read={read}
          time={createdAt}
          message={message}
          onClick={() => handleNotiClick(id, read, url)}
        />
      )),
    },
    {
      value: 'noti-unread',
      label: t('unread'),
      content:
        unreadNotifications.length === 0 ? (
          <Text size='sm' className='text-center text-dimGray-500 py-2'>
            {t('noNewNotifications')}
          </Text>
        ) : (
          unreadNotifications.map(({ id, message, createdAt, read }) => (
            <NotificationCard
              key={id}
              read={read}
              time={createdAt}
              message={message}
              onClick={() => handleNotiClick(id, read)}
            />
          ))
        ),
    },
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger>
        <div className='relative'>
          <BellIcon className='size-4 cursor-pointer' />
          <Show when={notifications.filter((noti) => !noti.read).length > 0}>
            <Dot
              variant='destructive'
              className='size-1 absolute top-0 right-0.5'
            />
          </Show>
        </div>
      </PopoverTrigger>
      <PopoverContent
        align={isMobile ? 'center' : 'end'}
        className={cn(
          'py-4 px-0 z-2000 w-[280px] max-h-[400px] border border-[#ECECEC] rounded-2xl shadow-[0px_4px_30px_0px_rgba(51,51,51,0.1)]',
          'md:w-[480px]',
        )}
      >
        <HStack className='items-center justify-between px-4'>
          <Text className='p-0 font-medium'>{t('notifications')}</Text>
          <span
            role='button'
            onClick={() => setOpen(false)}
            className='cursor-pointer relative z-50'
          >
            <XIcon className='size-4' />
          </span>
        </HStack>
        <Tabs defaultValue='noti-all' className='w-full'>
          <TabsList variant='underline' className='px-4 relative'>
            {tabs.map(({ value, label }) => {
              return (
                <TabsTrigger
                  variant='underline'
                  className='py-3'
                  key={value}
                  value={value}
                >
                  {label}
                </TabsTrigger>
              );
            })}
            <Button
              onClick={() => markAllNotiAsRead()}
              variant='unstyled'
              className='absolute p-0 right-4 text-blueBonnet-500 font-bold transition-colors hover:text-blueBonnet-600'
            >
              {t('markAsRead')}
            </Button>
          </TabsList>
          {tabs.map(({ value, content }) => {
            return (
              <TabsContent key={value} value={value}>
                <div className='max-h-[250px] overflow-y-auto'>{content}</div>
              </TabsContent>
            );
          })}
        </Tabs>
      </PopoverContent>
    </Popover>
  );
}
